import React, { useState, useEffect, useRef } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import API from "../../../services/API";


const AddTimetableModal = ({
    subjects,
    isOpen,
    onClose,
    classId,
    teachers,
    onAdded,
    selectedDay,
}) => {
    const [isSaving, setIsSaving] = useState(false);
    const [dayOfWeek, setDayOfWeek] = useState("");
    const [rotationWeek, setRotationWeek] = useState(1);
    const [entries, setEntries] = useState([]);
    const [existing, setExisting] = useState([]);
    const [editingRows, setEditingRows] = useState({});
    const [adding, setAdding] = useState(false);
    const savingRef = useRef(false);
    const [autoStart, setAutoStart] = useState("");
    const [autoEnd, setAutoEnd] = useState("");
    const [autoDuration, setAutoDuration] = useState(30);

    const [rehatStart, setRehatStart] = useState("");
    const [rehatDuration, setRehatDuration] = useState(20);
    // --- HELPER FUNCTIONS ---
    const NON_TEACHER_TAG_SUBJECTS = ["Rehat", "Perhimpunan", "Solat"];


    const toMins = (t) => {
        if (!t || typeof t !== 'string') return 0;
        const [h, m] = t.trim().split(":").map(Number);
        return h * 60 + m;
    };

    useEffect(() => {
        if (!isOpen) return;
        setDayOfWeek(selectedDay || "Monday");
        setEntries([]);
    }, [isOpen, selectedDay]);

    useEffect(() => {
        if (isOpen) loadExisting();
    }, [isOpen, dayOfWeek, rotationWeek]);

    const loadExisting = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await API.get("/timetable/list", {
                headers: { token },
                params: { class_id: classId, is_portal: true }
            });

            if (res.data.response_code === 1000) {
                const list = res.data.data.filter(
                    (i) => i.day_of_week === dayOfWeek && i.rotation_week === rotationWeek
                );
                const grouped = {};

                list.forEach(row => {
                    const key = `${row.start_time}_${row.end_time}`;
                    if (!grouped[key]) {
                        grouped[key] = {
                            start_time: row.start_time,
                            end_time: row.end_time,
                            subjects: []
                        };
                    }

                    grouped[key].subjects.push({
                        id: row.id,
                        subject_name: row.subject_name,
                        teacher_uuid: row.teacher_uuid,
                        remarks: row.remarks
                    });
                });

                setExisting(Object.values(grouped));

                const editMap = {};
                list.forEach(row => editMap[row.id] = { ...row });
                setEditingRows(editMap);
            }
        } catch (err) {
            console.error("Error loading existing", err);
        }
    };

    const handleSave = async () => {
        if (savingRef.current) {
            console.log("Duplicate blocked");
            return;
        }
        if (!dayOfWeek) return toast.error("Please select a day.");
        if (entries.length === 0) return toast.info("No new slots to add.");
        if (savingRef.current) return;

        savingRef.current = true;

        for (const session of entries) {
            if (!session.start_time || !session.end_time) {
                savingRef.current = false;
                return toast.error("Session time missing");
            }

            const startM = toMins(session.start_time);
            const endM = toMins(session.end_time);

            if (endM <= startM) {
                savingRef.current = false;
                return toast.error("End time must be after start time");
            }

            const NON_TEACHER_TAG_SUBJECTS = ["Rehat", "Perhimpunan", "Solat"];

            for (const subj of session.subjects) {
                const subject = (subj.subject_name || "").trim();

                if (!subject) {
                    savingRef.current = false;
                    return toast.error("Subject missing");
                }
                subj.teacher_uuid = subj.teacher_uuid || "";
                // Only require teacher for teaching subjects
                // if (!NON_TEACHER_TAG_SUBJECTS.includes(subject) && !subj.teacher_uuid) {
                //     savingRef.current = false;
                //     return toast.error(`Teacher required for ${subject}`);
                // }
            }
        }

        for (const session of entries) {
            for (const subj of session.subjects) {
                const exists = existing.some(s =>
                    s.start_time === session.start_time &&
                    s.end_time === session.end_time &&
                    s.subjects.some(es =>
                        es.subject_name === subj.subject_name &&
                        es.teacher_uuid === subj.teacher_uuid
                    )
                );

                if (exists) {
                    savingRef.current = false;
                    toast.error("This timetable slot already exists");
                    return;
                }
            }
        }

        // 🧹 Deduplicate payload
        const raw = entries.flatMap(session =>
            session.subjects.map(subj => ({
                class_id: classId,
                start_time: session.start_time,
                end_time: session.end_time,
                subject_name: subj.subject_name,
                teacher_uuid: subj.teacher_uuid,
                remarks: subj.remarks
            }))
        );

        const seen = new Set();
        const enriched = raw.filter(row => {
            const key = `${row.start_time}-${row.end_time}-${row.subject_name}-${row.teacher_uuid}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });

        setAdding(true);
        setIsSaving(true);

        try {
            const token = localStorage.getItem("token");
            const response = await API.post(
                "/timetable/add",
                { is_portal: true, day_of_week: dayOfWeek, rotation_week: Number(rotationWeek), entries: enriched },
                { headers: { token } }
            );

            if (response.data.response_code === 1000) {
                toast.success("Timetable updated successfully!");
                onAdded();
                loadExisting();
                setEntries([]);
            } else {
                toast.error(response.data.message);
            }
        } catch (err) {
            toast.error("Error saving timetable");
        } finally {
            setAdding(false);
            setIsSaving(false);
            savingRef.current = false;
        }
    };



    const saveExistingSession = async (session) => {
        try {
            const token = localStorage.getItem("token");

            // Split rows
            const toUpdate = session.subjects.filter(s => !!s.id);
            const toInsert = session.subjects.filter(s => !s.id);

            // UPDATE existing rows
            if (toUpdate.length > 0) {
                await Promise.all(
                    toUpdate.map(row =>
                        API.put(
                            `/timetable/edit/${row.id}`,
                            {
                                start_time: session.start_time,
                                end_time: session.end_time,
                                subject_name: row.subject_name,
                                teacher_uuid: row.teacher_uuid,
                                remarks: row.remarks,
                                rotation_week: Number(rotationWeek)
                            },
                            {
                                headers: { token, is_portal: true }
                            }
                        )
                    )
                );
            }

            // INSERT new parallel subjects
            if (toInsert.length > 0) {
                await API.post(
                    "/timetable/add",
                    {
                        is_portal: true,
                        day_of_week: dayOfWeek,
                        rotation_week: Number(rotationWeek),
                        entries: toInsert.map(subj => ({
                            class_id: classId,
                            start_time: session.start_time,
                            end_time: session.end_time,
                            subject_name: subj.subject_name,
                            teacher_uuid: subj.teacher_uuid,
                            remarks: subj.remarks
                        }))
                    },
                    {
                        headers: { token }
                    }
                );
            }

            await loadExisting();     // MUST await
            setExisting(prev => [...prev]);
            onAdded?.();

            toast.success("Existing slot updated");

        } catch (err) {
            console.error("Save existing session failed:", err);
            toast.error("Failed to save existing slot");
        }
    };
    const deleteSubject = async (subjectId, sIdx, subIdx) => {
        if (!window.confirm("Delete this subject?")) return;

        try {
            const token = localStorage.getItem("token");

            await API.delete(
                `/timetable/delete/${subjectId}?is_portal=true`,
                { headers: { token } }
            );

            // Remove from local UI immediately
            setExisting(prev => {
                const next = [...prev];
                next[sIdx].subjects.splice(subIdx, 1);

                // If no subjects left in this time slot → remove session
                if (next[sIdx].subjects.length === 0) {
                    next.splice(sIdx, 1);
                }

                return next;
            });

            toast.success("Subject deleted");
            onAdded?.();

        } catch (err) {
            console.error("Delete subject failed:", err);
            toast.error("Failed to delete subject");
        }
    };

    const isAM = (t) => {
        if (!t) return null;
        const h = Number(t.split(":")[0]);
        return h < 12;
    };

    const clampEndToSameHalfDay = (start, end) => {
        if (!start || !end) return end || "";

        const startHour = Number(start.split(":")[0]);
        const endHour = Number(end.split(":")[0]);

        const startIsAM = startHour < 12;
        const endIsAM = endHour < 12;

        // Only clamp if user crosses AM/PM boundary
        if (startIsAM !== endIsAM) {
            const [eh, em] = end.split(":").map(Number);

            let fixedHour = eh;

            if (startIsAM && eh >= 12) fixedHour = 11;   // clamp to 11:xx
            if (!startIsAM && eh < 12) fixedHour = 12;   // clamp to 12:xx

            return `${String(fixedHour).padStart(2, "0")}:${String(em).padStart(2, "0")}`;
        }

        return end; // allow editing within same AM/PM
    };
    const mins = (t) => {
        const [h, m] = t.split(":").map(Number);
        return h * 60 + m;
    };

    const fmt = (m) => {
        const h = Math.floor(m / 60);
        const mm = m % 60;
        return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
    };

    const autoGenerateSlots = () => {
        if (!autoStart || !autoEnd || !autoDuration) {
            return toast.error("Please fill start, end and duration");
        }

        let startM = mins(autoStart);
        const endM = mins(autoEnd);
        const rehatM = rehatStart ? mins(rehatStart) : null;

        const generated = [];

        while (startM + autoDuration <= endM) {
            const nextM = startM + autoDuration;

            // Inject REHAT
            if (rehatM !== null && startM === rehatM) {
                generated.push({
                    start_time: fmt(startM),
                    end_time: fmt(startM + rehatDuration),
                    subjects: [
                        { subject_name: "Rehat", teacher_uuid: "", remarks: "" }
                    ]
                });

                startM += rehatDuration;
                continue;
            }

            generated.push({
                start_time: fmt(startM),
                end_time: fmt(nextM),
                subjects: [
                    { subject_name: "", teacher_uuid: "", remarks: "" }
                ]
            });

            startM = nextM;
        }

        setEntries(generated);
        toast.success(`Generated ${generated.length} slots`);
    };
    return (
        <Modal isOpen={isOpen} toggle={onClose} size="xl" scrollable>
            <ModalHeader toggle={onClose} className="text-black">Manage Timetable – {dayOfWeek}</ModalHeader>
            <ModalBody className="text-black">
                <h6 className="fw-bold mb-3">Existing Slots</h6>
                {existing.length === 0 ? <p className="text-muted mb-4">No entries yet.</p> : (
                    existing.map((session, sIdx) => (
                        <div key={sIdx} className="text-black border rounded p-3 mb-3 bg-light shadow-sm">


                            <div className="row mb-3">
                                <div className="col-md-3">
                                    <label className="fw-bold">Start Time</label>
                                    <input
                                        type="time"
                                        className="form-control"
                                        value={session.start_time}
                                        onChange={(e) => {
                                            const next = [...existing];
                                            next[sIdx].start_time = e.target.value;
                                            setExisting(next);
                                        }}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <label className="fw-bold">End Time</label>
                                    <input
                                        type="time"
                                        className="form-control"
                                        value={session.end_time}
                                        onChange={(e) => {
                                            const next = [...existing];
                                            next[sIdx].end_time = e.target.value;
                                            setExisting(next);
                                        }}
                                    />
                                </div>
                            </div>

                            {session.subjects.map((subj, subIdx) => (
                                <div key={subIdx} className="row align-items-end mb-2">

                                    <div className="col-md-4">
                                        <Select
                                            options={subjects}
                                            value={subjects.find(s => s.value === subj.subject_name) || null}
                                            onChange={(opt) => {
                                                const next = [...existing];
                                                const val = opt?.value || "";

                                                next[sIdx].subjects[subIdx].subject_name = val;

                                                // Auto-clear teacher for non-teaching subjects
                                                if (NON_TEACHER_TAG_SUBJECTS.includes(val)) {
                                                    next[sIdx].subjects[subIdx].teacher_uuid = "";
                                                }

                                                setExisting(next);
                                            }}

                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <Select
                                            options={teachers}
                                            value={teachers.find(t => t.value === subj.teacher_uuid) || null}
                                            onChange={(opt) => {
                                                const next = [...existing];
                                                next[sIdx].subjects[subIdx].teacher_uuid = opt?.value || "";
                                                setExisting(next);
                                            }}
                                        />
                                    </div>

                                    <div className="col-md-2 text-end">
                                        {subj.id && (
                                            <Button
                                                size="sm"
                                                color="danger"
                                                outline
                                                onClick={() => deleteSubject(subj.id, sIdx, subIdx)}
                                            >
                                                Delete
                                            </Button>
                                        )}
                                    </div>

                                </div>
                            ))}


                            {/* ADD PARALLEL SUBJECT */}
                            <Button
                                size="sm"
                                color="primary"
                                outline
                                onClick={() => {
                                    if (session.subjects.length >= 3) {
                                        toast.error("Maximum 3 parallel subjects allowed");
                                        return;
                                    }

                                    const next = [...existing];
                                    next[sIdx].subjects.push({
                                        subject_name: "",
                                        teacher_uuid: "",
                                        remarks: ""
                                    });
                                    setExisting(next);
                                }}
                            >
                                + Add Parallel Subject
                            </Button>
                            <Button
                                size="sm"
                                color="primary"
                                className="mt-2 ms-2"
                                onClick={() => saveExistingSession(session)}
                            >
                                Save Changes
                            </Button>

                        </div>
                    ))
                )}
                <hr className="my-4" />
                <h6 className="fw-bold mt-3">Auto Generate Slots</h6>

                <div className="row mb-3">
                    <div className="col-md-3">
                        <label>Start Time</label>
                        <input type="time" className="form-control" value={autoStart} onChange={e => setAutoStart(e.target.value)} />
                    </div>

                    <div className="col-md-3">
                        <label>End Time</label>
                        <input type="time" className="form-control" value={autoEnd} onChange={e => setAutoEnd(e.target.value)} />
                    </div>

                    <div className="col-md-3">
                        <label>Slot Duration (min)</label>
                        <input type="number" className="form-control" value={autoDuration} onChange={e => setAutoDuration(Number(e.target.value))} />
                    </div>

                    <div className="col-md-3">
                        <label>Rehat Duration (min)</label>
                        <input type="number" className="form-control" value={rehatDuration} onChange={e => setRehatDuration(Number(e.target.value))} />
                    </div>
                </div>

                <div className="row mb-3">
                    <div className="col-md-3">
                        <label>Rehat Start</label>
                        <input type="time" className="form-control" value={rehatStart} onChange={e => setRehatStart(e.target.value)} />
                    </div>

                    <div className="col-md-3 d-flex align-items-end">
                        <Button color="primary" outline onClick={autoGenerateSlots}>
                            Auto Generate
                        </Button>
                    </div>
                </div>
                <h6 className="fw-bold mt-4 mb-3">Add New Slot</h6>
                {entries.map((session, sIdx) => (
                    <div key={sIdx} className="border rounded p-3 mb-3 bg-white shadow-sm">

                        {/* SESSION TIME (shared) */}
                        <div className="row mb-3">
                            <div className="col-md-3">
                                <label className="fw-bold">Start Time</label>
                                <input
                                    type="time"
                                    className="form-control"
                                    value={session.start_time}
                                    onChange={(e) => {
                                        const next = [...entries];
                                        const start = e.target.value;

                                        next[sIdx].start_time = start;

                                        // Only auto-fill end time if empty
                                        if (!next[sIdx].end_time) {
                                            const [h, m] = start.split(":").map(Number);
                                            const eh = h < 11 ? h + 1 : h; // default +1h but stay in AM/PM
                                            next[sIdx].end_time = `${String(eh).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
                                        }

                                        setEntries(next);
                                    }}
                                />
                            </div>

                            <div className="col-md-3">
                                <label className="fw-bold">End Time</label>
                                <input
                                    type="time"
                                    className="form-control"
                                    value={session.end_time}
                                    onChange={(e) => {
                                        const next = [...entries];
                                        next[sIdx].end_time = clampEndToSameHalfDay(
                                            next[sIdx].start_time,
                                            e.target.value
                                        );
                                        setEntries(next);
                                    }}
                                />
                            </div>
                            <div className="col-md-3 d-flex align-items-end">
                                <Button
                                    color="danger"
                                    outline
                                    onClick={() => {
                                        const next = [...entries];
                                        next.splice(sIdx, 1);
                                        setEntries(next);
                                    }}
                                >
                                    Remove Slot
                                </Button>
                            </div>
                        </div>

                        {/* SUBJECTS */}
                        {session.subjects.map((subj, subIdx) => (
                            <div key={subIdx} className="row align-items-end mb-2">

                                <div className="col-md-4">
                                    <label>Subject</label>
                                    <Select
                                        options={subjects}
                                        value={subjects.find(s => s.value === subj.subject_name) || null}
                                        onChange={(opt) => {
                                            const next = [...entries];
                                            const val = opt?.value || "";

                                            next[sIdx].subjects[subIdx].subject_name = val;

                                            if (NON_TEACHER_TAG_SUBJECTS.includes(val)) {
                                                next[sIdx].subjects[subIdx].teacher_uuid = "";
                                            }

                                            setEntries(next);
                                        }}
                                    />

                                </div>

                                <div className="col-md-4">
                                    <label>Teacher</label>
                                    <Select
                                        options={teachers}
                                        value={teachers.find(t => t.value === subj.teacher_uuid) || null}
                                        onChange={(opt) => {
                                            const next = [...entries];
                                            next[sIdx].subjects[subIdx].teacher_uuid = opt?.value || "";
                                            setEntries(next);
                                        }}
                                    />
                                </div>

                                <div className="col-md-2">
                                    <Button
                                        color="outline-danger"
                                        onClick={() => {
                                            const next = [...entries];
                                            next[sIdx].subjects.splice(subIdx, 1);
                                            setEntries(next);
                                        }}

                                    >
                                        Remove
                                    </Button>
                                </div>

                            </div>
                        ))}

                        {/* ADD PARALLEL SUBJECT */}
                        <Button
                            size="sm"
                            color="primary"
                            outline
                            onClick={() => {
                                if (session.subjects.length >= 3) {
                                    toast.error("Maximum 3 parallel subjects allowed");
                                    return;
                                }

                                const next = [...entries];
                                next[sIdx].subjects.push({
                                    subject_name: "",
                                    teacher_uuid: "",
                                    remarks: ""
                                });
                                setEntries(next);
                            }}
                        >
                            + Add Parallel Subject
                        </Button>


                    </div>
                ))}

                <div className="text-center mt-3">
                    <Button color="success" outline onClick={() => {

                        setEntries([
                            ...entries,
                            {
                                start_time: "",
                                end_time: "",
                                subjects: [
                                    { subject_name: "", teacher_uuid: "", remarks: "" }
                                ]
                            }
                        ]);

                    }}>
                        + Add Another Slot
                    </Button>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button
                    color="primary"
                    disabled={savingRef.current}
                    onClick={handleSave}
                >
                    {savingRef.current ? "Saving..." : "Save Timetable"}
                </Button>

                <Button color="secondary" onClick={onClose}>Close</Button>
            </ModalFooter>
        </Modal>
    );
};

export default AddTimetableModal;